<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('users.usersLabel') }}</span>
        <h3 class="page-title">{{ $t('users.addingNewUser') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/uzytkownicy')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button theme="success" @click.prevent="validateForm">
          <i class="material-icons">add</i>
          {{ $t('users.addUser') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs v-sticky-tabs card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="user-new-form" tag="form">
              <div class="form-group">
                <label for="name">
                  {{ $t('ui.nameAndSurename') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="email">
                  {{ $t('ui.eMailCap') }}:
                </label>

                <validation-provider
                  rules="required|email"
                  name="email"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    type="email"
                    v-model="email" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="email">
                  {{ $t('login.password') }}:
                </label>

                <validation-provider
                  rules="required|min:6"
                  name="original-password"
                  v-slot="{ errors }">
                  <d-input
                    type="password"
                    :state="errors.length ? 'invalid' : null"
                    v-model="password" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="email">
                  {{ $t('ui.repeatPassword') }}:
                </label>

                <validation-provider
                  rules="required|password:original-password"
                  name="password-confirmation"
                  v-slot="{ errors }">
                  <d-input
                    type="password"
                    :state="errors.length ? 'invalid' : null"
                    v-model="passwordConfirmation" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label class="d-block">
                  {{ $t('users.uiLanguage') }}
                </label>
                <d-form-radio inline v-model="language_id" value="1">
                  {{ $t('users.uiLanguagePolish') }}
                </d-form-radio>
                <d-form-radio inline v-model="language_id" value="2">
                  {{ $t('users.uiLanguageEnglish') }}
                </d-form-radio>
              </div>

              <div class="form-group">
                <label class="d-block">
                  {{ $t('users.accountType') }}
                </label>
                <d-form-radio inline v-model="type" value="admin">
                  {{ $t('users.admin') }}
                </d-form-radio>
                <d-form-radio inline v-model="type" value="worker">
                  {{ $t('users.worker') }}
                </d-form-radio>
                <d-form-radio inline v-model="type" value="warehouse_manager">
                  {{ $t('users.warehouseman') }}
                </d-form-radio>
                <d-form-radio inline v-model="type" value="warehouseman">
                  {{ $t('users.realWarehouseman') }}
                </d-form-radio>
                <d-form-radio inline v-model="type" value="customer">
                  {{ $t('users.customer') }}
                </d-form-radio>
              </div>

              <div
                v-if="type === 'warehouseman'"
                class="form-group">
                <label for="name">
                  {{ $t('users.pinCode') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="pinCode"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="pinCode" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div
                v-show="type === 'customer'"
                class="form-group">
                <customer-select
                  ref="customer-select"
                  :hasUserToLoad="false" />
              </div>

              <div class="form-group mt-1 mb-1">
                <d-checkbox inline v-model="active" toggle toggle-small>
                  {{ $t('users.isAccountActive') }}
                </d-checkbox>
              </div>

              <div
                v-show="type === 'worker'"
                class="form-group mt-1 mb-1">
                <d-checkbox inline v-model="sendSalesReport" toggle toggle-small>
                  {{ $t('users.sendSalesReport') }}
                </d-checkbox>
              </div>

              <div class="form-group mt-4">
                <label>
                  {{ $t('settings.userGroups.userGroups') }}:
                </label>

                <ul class="user-groups-list">
                  <li
                    v-for="(group, index) of userGroups"
                    :key="'user-group-' + index">
                    <d-checkbox
                      inline
                      v-model="groups[group.id]">
                      {{ group.name }}
                    </d-checkbox>
                  </li>
                </ul>
              </div>

              <div
                v-show="type === 'admin' || type === 'worker'"
                class="form-group mt-5">
                <label>
                  {{ $t('users.orderTags') }}:
                </label>

                <ul class="user-tags-list">
                  <li
                    v-for="(tag, index) of orderTags"
                    :key="'order-tag-' + index">
                    <d-checkbox
                      inline
                      v-model="tags[tag.id]">
                      <d-badge :style="'background-color:' + tag.color + '; color: ' + contrastColor(tag.color) + ';'">
                        {{ tag.name }}
                      </d-badge>
                    </d-checkbox>
                  </li>
                </ul>
              </div>
            </validation-observer>

            <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center">
              {{ $t('ui.dataLoading') }}&hellip;
            </div>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
            </d-alert>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import CustomerSelect from './CustomerSelect.vue';
import Vue from 'vue';

export default {
  name: 'user-new',
  components: {
    'customer-select': CustomerSelect
  },
  computed: {
    allDataLoaded () {
      return this.loadedOrderTags && this.loadedUserGroups;
    }
  },
  data () {
    return {
      name: '',
      email: '',
      type: 'worker',
      language_id: '1',
      active: true,
      password: '',
      passwordConfirmation: '',
      pinCode: '',
      sendSalesReport: false,
      tags: {},
      groups: {},
      // additional data
      orderTags: [],
      userGroups: [],
      // flags
      loadedOrderTags: false,
      loadedUserGroups: false,
      loadError: false
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['user-new-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = {
        name: this.name,
        email: this.email,
        type: this.type,
        active: this.active ? 1 : 0,
        password: this.password,
        language_id: this.language_id,
        password_confirmation: this.passwordConfirmation,
        groups: this.prepareGroups()
      };

      if (this.type === 'customer') {
        formDataToSend.customer_id = this.$refs['customer-select'].customer_id;

        if (!formDataToSend.customer_id) {
          Vue.swal({
            title: this.$t('ui.saveErrorTitle'),
            html: this.$t('users.customerNotSelectedErrorText'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
          return;
        }
      }

      if (this.type === 'warehouseman') {
        formDataToSend.pin_code = this.pinCode;
      }

      if (this.type === 'worker' || this.type === 'admin') {
        formDataToSend.order_tags = this.prepareTags();
      }

      if (this.type === 'worker') {
        formDataToSend.send_sales_report = this.sendSalesReport;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/users/store',
        formData: formDataToSend,
        successTitle: this.$t('users.addUserSuccessTitle'),
        successText: this.$t('users.addUserSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('users.addUserErrorText'),
        successAction: () => {
          this.$router.push('/uzytkownicy')
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/orders/tags/items',
        loadedKey: 'loadedOrderTags',
        outputKey: 'orderTags',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/users/groups/items',
        loadedKey: 'loadedUserGroups',
        outputKey: 'userGroups',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });
    },
    contrastColor (colorBg) {
      let threshold = 130;
      let r = parseInt(colorBg.replace('#', '').substring(0, 2), 16);
      let g = parseInt(colorBg.replace('#', '').substring(2, 4), 16);
      let b = parseInt(colorBg.replace('#', '').substring(4, 6), 16);
      let brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;

      if (brightness > threshold) {
        return '#000';
      }

      return '#fff';
    },
    prepareTags () {
      let output = [];
      let tagIDs = Object.keys(this.tags);

      for (let i = 0; i < tagIDs.length; i++) {
        if (this.tags[tagIDs[i]]) {
          output.push(tagIDs[i]);
        }
      }

      return output;
    },
    prepareGroups () {
      let output = [];
      let groupIDs = Object.keys(this.groups);

      for (let i = 0; i < groupIDs.length; i++) {
        if (this.groups[groupIDs[i]]) {
          output.push(groupIDs[i]);
        }
      }

      return output;
    }
  }
};
</script>

<style lang="scss" scoped>
.user-tags-list {
  background: #f8f8f8;
  border: 1px solid #eee;
  border-radius: 5px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  list-style-type: none;
  margin: 0;
  padding: 20px 20px 10px 20px;

  li {
    font-size: 12px;
  }
}

.user-groups-list {
  background: #f8f8f8;
  border: 1px solid #eee;
  border-radius: 5px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  list-style-type: none;
  margin: 0;
  padding: 20px 20px 10px 20px;

  li {
    font-size: 12px;
  }
}
</style>
